/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-image/bulbimagecommand
 */

import { Command, ModelPosition } from 'ckeditor5';
import type { ModelWriter, ModelElement, ModelSelectable } from 'ckeditor5';
import { getDisplayedImageAsset } from './bulbimageutils';

/**
 * The bulb image command, the actual insert capabilities of the plugin.
 *
 * @extends Command
 */
export class InsertBulbImageCommand extends Command {

  public override execute(
    blockDetails: { id: any; version: any; },
    nextIndex: number,
    placeholderElement: ModelElement,
    insertPosition: ModelSelectable,
  ): void {
    this.editor.model.change((writer: ModelWriter): void => {
      // Check for an insertPosition, this would only happen if we had to transcode the asset and had the
      // placeholder and need to put this exactly where the placeholder was.
      if (insertPosition) {
        this.editor.model.insertContent(InsertBulbImageCommand.createImageBlock(writer, blockDetails), insertPosition);
      } else {
        this.editor.model.insertContent(InsertBulbImageCommand.createImageBlock(writer, blockDetails));
      }
      CKEBulbData.service.ckeStateService.insertParagraphSubject.next(true);
      this.handleMultipleInsertion(nextIndex);
      if (placeholderElement) {
        writer.remove(placeholderElement);
      }
    });
  }

  /**
   * Method for creating image element.
   *
   * @param writer
   * @param blockDetails Contains id and url for image block.
   *
   */
  static createImageBlock(writer: ModelWriter, blockDetails: { id: any; version: any; }) {
    return writer.createElement('image', {
      'bulb-block-id': blockDetails.id,
      'bulb-block-version': blockDetails.version,
      src: getDisplayedImageAsset(blockDetails).url,
    });
  }

  public override refresh(): void {
    const { model } = this.editor;
    const { selection } = model.document;
    const allowedIn = model.schema.findAllowedParent(selection.getFirstPosition() as ModelPosition, 'image');

    this.set('isEnabled', allowedIn !== null);
  }

  /**
   * This code will handle multiple image insertion.
   * @param nextIndex
   */
  private handleMultipleInsertion(nextIndex: number): void {
    if (nextIndex !== undefined && nextIndex > -1) {
      CKEBulbData.service.ckeStateService.setMultipleImage(true);
    } else if (nextIndex === -1) {
      CKEBulbData.service.ckeStateService.multipleUploadStart.next(-1);
      CKEBulbData.service.ckeStateService.handleSaveOperation.next(true);
    }
    // listen paragraph insert to insert next image
    const paragraphInsertSubscribe = CKEBulbData.service.ckeStateService.insertParagraphSubject.subscribe((value) => {
      if (!value) {
        if (nextIndex) {
          if (nextIndex > 0) {
            setTimeout(() => {
              // Will call next image to insert into page based on index value.
              // @ts-ignore
              CKEBulbData.service.ckeStateService.multipleUploadStart.next(nextIndex);
            }, 0);
          }
        }
        paragraphInsertSubscribe.unsubscribe();
      }
    });
  }
}
